import { BoxSubscriptionEntity } from "@modules/boxSubscription/entities/boxSubscriptionEntity"
import {
  convertCouponInputToRequest,
  convertPauseInputToRequest,
  CouponInputFormik,
  PauseInputFormik,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { useServicesContext } from "@modules/core/services/services.context"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"

export const useBoxSubscriptions = (memberId: string) => {
  const { boxSubscriptionApi } = useServicesContext()
  const { data } = useSuspenseQuery({
    queryKey: ["boxSubscription", memberId],
    queryFn: async () => {
      return await boxSubscriptionApi.getBoxSubscriptions(memberId)
    },
  })

  return {
    data: {
      ...data,
      content: data.content.map((boxSubcription) => BoxSubscriptionEntity.fromItemDto(boxSubcription)) || [],
    },
  }
}

export const useDeleteBoxSubscription = (memberId: string) => {
  const { boxSubscriptionApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: deleteSubscription, isPending: isDeleteSubscriptionPending } = useMutation({
    mutationFn: async ({
      subscriptionId,
      cancelAtPeriodEnd,
      endPeriodInMonths,
    }: {
      subscriptionId: string
      cancelAtPeriodEnd: boolean
      endPeriodInMonths?: number
    }) => {
      return await boxSubscriptionApi.deleteSubscription(memberId, subscriptionId, cancelAtPeriodEnd, endPeriodInMonths)
    },
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["boxSubscription"],
        })
        queryClient.invalidateQueries({
          queryKey: ["boxMember", memberId],
        })
        queryClient.invalidateQueries({
          queryKey: ["boxMembers"],
        })
        queryClient.invalidateQueries({
          queryKey: ["boxPurchases", memberId],
        })
      }, 4000) // Wait for the subscription to be deleted
    },
  })

  return {
    deleteSubscription,
    isDeleteSubscriptionPending,
  }
}

export const useBoxSubscriptionActions = (memberId: string, subscriptionId: string) => {
  const { boxSubscriptionApi } = useServicesContext()
  const queryClient = useQueryClient()

  // Create pause
  const { mutateAsync: pauseSubscription, isPending: isPauseSubscriptionLoading } = useMutation({
    mutationFn: async (pause: PauseInputFormik) => {
      return await boxSubscriptionApi.pauseSubscription(memberId, subscriptionId, convertPauseInputToRequest(pause))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxSubscription"],
      })
      queryClient.invalidateQueries({
        queryKey: ["boxPurchases", memberId],
      })
    },
  })

  // Resume subscription
  const { mutateAsync: resumeSubscription, isPending: isResumeSubscriptionLoading } = useMutation({
    mutationFn: async () => {
      return await boxSubscriptionApi.resumeSubscription(memberId, subscriptionId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxSubscription"],
      })
      queryClient.invalidateQueries({
        queryKey: ["boxPurchases", memberId],
      })
    },
  })

  // Apply discount
  const { mutateAsync: applyDiscount, isPending: isApplyDiscountLoading } = useMutation({
    mutationFn: async (coupon: CouponInputFormik) => {
      return await boxSubscriptionApi.applySubscriptionDiscount(
        memberId,
        subscriptionId,
        convertCouponInputToRequest(coupon),
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxSubscription"],
      })
      queryClient.invalidateQueries({
        queryKey: ["boxPurchases", memberId],
      })
    },
  })

  // Remove discount
  const { mutateAsync: removeDiscount, isPending: isRemoveDiscountLoading } = useMutation({
    mutationFn: async () => {
      return await boxSubscriptionApi.removeSubscriptionDiscount(memberId, subscriptionId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxSubscription"],
      })
      queryClient.invalidateQueries({
        queryKey: ["boxPurchases", memberId],
      })
    },
  })

  // Loading
  const isPending =
    isPauseSubscriptionLoading || isResumeSubscriptionLoading || isApplyDiscountLoading || isRemoveDiscountLoading

  return {
    pauseSubscription,
    resumeSubscription,
    applyDiscount,
    removeDiscount,
    isPending,
  }
}
